import { ErrorMessage, Field, Formik } from "formik";
import React, { useState } from "react";
import * as Yup from "yup";

import { IIdentity } from "@/customTypings/user/Identity";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { userService } from "../../../../services";
import { Avatar } from "../../../common/Avatar/Avatar";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import { showToastSuccess, showToastError } from "../../../../utils";

type Props = {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<any>>;
    user: IIdentity;
    color: string;
    setUserInteraction?: React.Dispatch<React.SetStateAction<any>>;
};

const UserColorEdit: React.FC<Props> = ({
    show,
    setShow,
    user,
    color,
    setUserInteraction,
}) => {
    const [alertVariant, setAlertVariant] = useState("danger");

    const handleClose = () => setShow(false);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            <Formik
                initialValues={{
                    color: color ?? "",
                }}
                validationSchema={Yup.object().shape({
                    color: Yup.string().required("Colour is required"),
                })}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    userService.updateColor(user.id, values.color).then(
                        (response: any) => {
                            setSubmitting(false);
                            if (response.status !== "Failure") {
                                if (setUserInteraction !== undefined) {
                                    setUserInteraction(true);
                                }
                                showToastSuccess("Color updated successfully!");
                                    setTimeout(() => handleClose(), 1000);
                                } else {
                                    setAlertVariant("danger");
                                    showToastError(response.message);
                                }
                            },
                            (error: any) => {
                                setAlertVariant("danger");
                                if (error.status === 400 && error.errors) {
                                    Object.keys(error.errors).forEach((key) => {
                                        const messages = error.errors[key];
                                        if (Array.isArray(messages)) {
                                            messages.forEach((message) => showToastError(message));
                                        }
                                    });
                                } else {
                                    showToastError("An unexpected error occurred!");
                                }
                                setSubmitting(false);
                            }
                    );
                }}
            >
                {({ errors, touched, status, isSubmitting, handleSubmit }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Colour</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <h5 className="mb-3">
                                <Avatar identity={user} />
                            </h5>

                            <Form.Group
                                as={Row}
                                className="mb-3"
                                controlId="color"
                            >
                                <Form.Label column sm={3}>
                                    Colour <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col sm={9}>
                                    <Field
                                        name="color"
                                        type="color"
                                        autoFocus
                                        className={
                                            "form-control" +
                                            (errors.color && touched.color
                                                ? " is-invalid"
                                                : "")
                                        }
                                    />
                                    <ErrorMessage
                                        name="color"
                                        component="div"
                                        className="invalid-feedback"
                                    />
                                </Col>
                            </Form.Group>
                            {status && (
                                <Alert variant={alertVariant} className="mt-3">
                                    {status}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="form-group">
                                <Button
                                    variant="primary"
                                    disabled={isSubmitting}
                                    type="submit"
                                    className="me-2"
                                >
                                    {isSubmitting ? (
                                        <LoadingSpinner text="Updating..." />
                                    ) : (
                                        "Update"
                                    )}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export { UserColorEdit };
